import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

import BaseNavbar from "../../commanComponents/navbar/baseNavbar";
import Footer from "../../commanComponents/footer/footer";
import SkeletonLoading from "../../commanComponents/skeletonLoading/skeletonLoading";
import {
  bookCheckoutUserListService,
  bookOrderCreateService,
  bookOrderVerifyPaymentService,
  bookOrderFailureService,
  bookOrderRetryService,
} from "../../../modules/services";
import "./bookCheckoutPage.css";
import { ROUTES } from "../../../constant";

const BookPaymentPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [notification, setNotification] = useState({
    message: "",
    visible: false,
    type: "success",
  });
  const [currentStep, setCurrentStep] = useState(1); // 1: Details, 2: Payment, 3: Confirmation

  // Order tracking
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null); // pending, processing, paid, failed
  const [paymentError, setPaymentError] = useState(null);
  const [canRetry, setCanRetry] = useState(false);

  // Form states
  const [billingDetails, setBillingDetails] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
  });

  const [paymentDetails, setPaymentDetails] = useState({
    razorpay_order_id: "",
    razorpay_payment_id: "",
    razorpay_signature: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [searchParams] = useSearchParams();

  const bookId = searchParams.get("book_id");
  const navigate = useNavigate();
  const location = useLocation();

  const goToMyBooks = () => {
    navigate(ROUTES.BOOKS_SELLING_BOOK_DASHBOARD);
  };

  const fetchCartItems = useCallback(async () => {
    try {
      setLoading(true);
      let bookIdParam = {};
      if (bookId !== null) {
        bookIdParam = { book_id: bookId };
      }

      const responseData = await bookCheckoutUserListService(bookIdParam);

      if (responseData === "error" || !responseData.data) {
        showNotification("Failed to load cart items", "error");
        return;
      }

      if (
        responseData.status_code !== 200 &&
        responseData.status_code !== 201
      ) {
        showNotification("Failed to load cart items", "error");
        return;
      }

      setCartItems(responseData.data.books);
    } catch (error) {
      console.error("Error fetching cart:", error);
      showNotification("Failed to load cart items", "error");
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Fetch cart items
  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  // Fade-in animation
  useEffect(() => {
    const handleScroll = () => {
      document.querySelectorAll(".payment-fade-in").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          el.classList.add("visible");
        }
      });
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const showNotification = (message, type = "success") => {
    setNotification({ message, visible: true, type });
    setTimeout(
      () => setNotification({ message: "", visible: false, type: "success" }),
      5000
    );
  };

  // Calculate order details
  const calculateSubtotal = () => {
    return (
      cartItems?.reduce((sum, item) => sum + parseFloat(item.price), 0) || 0
    );
  };

  const calculateDiscount = () => {
    return location.state?.discount || 0;
  };

  const calculateShipping = () => {
    const subtotal = calculateSubtotal();
    return subtotal > 500 ? 0 : 50;
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateShipping();
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!billingDetails.first_name.trim())
      errors.first_name = "First name is required";
    if (!billingDetails.last_name.trim())
      errors.last_name = "Last name is required";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!billingDetails.email.trim()) {
      errors.email = "Email is required";
    } else if (!emailRegex.test(billingDetails.email)) {
      errors.email = "Invalid email format";
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!billingDetails.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!phoneRegex.test(billingDetails.phone)) {
      errors.phone = "Invalid phone number (10 digits)";
    }

    if (!billingDetails.address.trim()) errors.address = "Address is required";
    if (!billingDetails.city.trim()) errors.city = "City is required";
    if (!billingDetails.state.trim()) errors.state = "State is required";

    const pincodeRegex = /^\d{6}$/;
    if (!billingDetails.pincode.trim()) {
      errors.pincode = "Pincode is required";
    } else if (!pincodeRegex.test(billingDetails.pincode)) {
      errors.pincode = "Invalid pincode (6 digits)";
    }

    if (!agreeTerms) {
      errors.terms = "You must agree to terms and conditions";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBillingDetails((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleContinueToPayment = () => {
    if (validateForm()) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: "smooth" });
      showNotification("Billing details confirmed! Proceed with payment.");
    } else {
      showNotification("Please fill all required fields correctly", "error");
    }
  };

  // Create Razorpay order (API call to backend)
  const createRazorpayOrder = async () => {
    try {
      const total = calculateTotal();

      const responseData = await bookOrderCreateService(
        {
          book_ids: cartItems.map((item) => item.id),
          additional_data: billingDetails,
          amount: total,
          currency: "INR",
        },
        {}
      );

      if (responseData === "error" || !responseData.data) {
        throw new Error("Failed to create order");
      }

      if (
        responseData.status_code !== 200 &&
        responseData.status_code !== 201
      ) {
        throw new Error(responseData.message?.[0] || "Failed to create order");
      }

      // Store current order
      setCurrentOrder(responseData.data);
      setOrderStatus("pending");

      return {
        key: responseData.data.key_id,
        id: responseData.data.order_number, // Razorpay order ID
        amount: Math.round(parseFloat(responseData.data.total_amount) * 100), // Convert to paise
        currency: responseData.data.payment_data?.currency || "INR",
        orderId: responseData.data.id, // Internal order ID
      };
    } catch (error) {
      console.error("Order creation error:", error);
      throw error;
    }
  };

  // Razorpay Payment Handler
  const handleRazorpayPayment = async () => {
    if (!window.Razorpay) {
      showNotification(
        "Razorpay SDK not loaded. Please refresh the page.",
        "error"
      );
      return;
    }

    setProcessingPayment(true);
    setPaymentError(null);

    try {
      // Step 1: Create order on backend (status: pending)
      const orderData = await createRazorpayOrder();

      if (!orderData || !orderData.id) {
        throw new Error("Failed to create order");
      }

      // Step 2: Initialize Razorpay payment (status: processing)
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "FIZ Robotic Solutions",
        description: `Purchase of ${cartItems?.length} book(s)`,
        order_id: orderData.id,

        handler: async function (response) {
          // Payment successful - verify signature
          setOrderStatus("processing");
          await verifyPayment(response);
        },

        prefill: {
          name: `${billingDetails.first_name} ${billingDetails.last_name}`,
          email: billingDetails.email,
          contact: billingDetails.phone,
        },

        notes: {
          address: billingDetails.address,
          city: billingDetails.city,
          state: billingDetails.state,
          pincode: billingDetails.pincode,
        },

        theme: {
          color: "#293659",
        },

        modal: {
          ondismiss: async function () {
            // User closed payment modal without completing
            setProcessingPayment(false);
            setOrderStatus("pending");

            await handlePaymentFailure(
              orderData.id,
              "PAYMENT_CANCELLED",
              "Payment cancelled by user"
            );

            showNotification("Payment cancelled", "warning");
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      // Handle payment failure at gateway level
      razorpay.on("payment.failed", async function (response) {
        console.error("Payment failed:", response.error);

        await handlePaymentFailure(
          orderData.id,
          response.error.code,
          response.error.description
        );

        setProcessingPayment(false);
        showNotification(
          `Payment failed: ${response.error.description}`,
          "error"
        );
      });

      razorpay.open();
    } catch (error) {
      console.error("Payment initialization error:", error);
      showNotification(
        error.message || "Payment initialization failed. Please try again.",
        "error"
      );
      setProcessingPayment(false);
      setOrderStatus("failed");
    }
  };

  // Verify payment (status: paid)
  const verifyPayment = async (response) => {
    try {
      setOrderStatus("processing");

      const verificationResponse = await bookOrderVerifyPaymentService(
        {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        },
        {}
      );

      if (verificationResponse === "error" || !verificationResponse.data) {
        throw new Error("Payment verification failed");
      }

      if (
        verificationResponse.status_code !== 200 &&
        verificationResponse.status_code !== 201
      ) {
        throw new Error(
          verificationResponse.message?.[0] || "Payment verification failed"
        );
      }

      // Payment verified successfully
      console.log("Payment verified:", verificationResponse.data);

      setOrderStatus("paid");
      setCurrentStep(3);
      setPaymentDetails(response);
      setProcessingPayment(false);
      window.dispatchEvent(new Event("cartUpdated"));

      showNotification(
        "🎉 Payment successful! Books added to your library.",
        "success"
      );

      setTimeout(() => {
        goToMyBooks();
      }, 2000);
    } catch (error) {
      console.error("Verification error:", error);

      // Mark order as failed on backend
      if (currentOrder) {
        await handlePaymentFailure(
          currentOrder.order_number,
          "VERIFICATION_FAILED",
          error.message
        );
      }

      setOrderStatus("failed");
      setProcessingPayment(false);

      showNotification(
        "Payment verification failed. Please contact support with your payment ID.",
        "error"
      );
    }
  };

  // Handle payment failure (status: failed)
  const handlePaymentFailure = async (
    razorpayOrderId,
    errorCode,
    errorDescription
  ) => {
    try {
      const failureResponse = await bookOrderFailureService(
        {
          razorpay_order_id: razorpayOrderId,
          error_code: errorCode,
          error_description: errorDescription,
        },
        {}
      );

      if (failureResponse && failureResponse.data) {
        setCanRetry(failureResponse.data.can_retry);
        setPaymentError({
          code: errorCode,
          description: errorDescription,
        });
        setOrderStatus("failed");
      }
    } catch (error) {
      console.error("Error reporting payment failure:", error);
    }
  };

  // Retry failed payment
  const handleRetryPayment = async () => {
    if (!currentOrder) {
      showNotification("No order found to retry", "error");
      return;
    }

    try {
      setProcessingPayment(true);
      setPaymentError(null);

      // Call retry API to create new Razorpay order
      const retryResponse = await bookOrderRetryService(
        currentOrder.id,
        { currency: "INR" },
        {}
      );

      if (retryResponse === "error" || !retryResponse.data) {
        throw new Error("Failed to retry payment");
      }

      if (
        retryResponse.status_code !== 200 &&
        retryResponse.status_code !== 201
      ) {
        throw new Error(
          retryResponse.message?.[0] || "Failed to retry payment"
        );
      }

      // Update current order
      setCurrentOrder(retryResponse.data);
      setOrderStatus("pending");

      // Initialize Razorpay with new order
      const orderData = {
        key: retryResponse.data.key_id,
        id: retryResponse.data.order_id,
        amount: retryResponse.data.amount,
        currency: retryResponse.data.currency,
      };

      // Reuse the Razorpay payment handler
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "FIZ Robotic Solutions",
        description: `Retry: Purchase of ${cartItems?.length} book(s)`,
        order_id: orderData.id,

        handler: async function (response) {
          setOrderStatus("processing");
          await verifyPayment(response);
        },

        prefill: {
          name: `${billingDetails.first_name} ${billingDetails.last_name}`,
          email: billingDetails.email,
          contact: billingDetails.phone,
        },

        theme: {
          color: "#293659",
        },

        modal: {
          ondismiss: async function () {
            setProcessingPayment(false);
            setOrderStatus("pending");
            showNotification("Payment cancelled", "warning");
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", async function (response) {
        await handlePaymentFailure(
          orderData.id,
          response.error.code,
          response.error.description
        );
        setProcessingPayment(false);
      });

      razorpay.open();
    } catch (error) {
      console.error("Retry payment error:", error);
      showNotification(
        error.message || "Failed to retry payment. Please try again.",
        "error"
      );
      setProcessingPayment(false);
    }
  };

  const handlePlaceOrder = () => {
    // Load Razorpay SDK if not already loaded
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        handleRazorpayPayment();
      };
      script.onerror = () => {
        showNotification(
          "Failed to load payment gateway. Please refresh the page.",
          "error"
        );
        setProcessingPayment(false);
      };
      document.body.appendChild(script);
    } else {
      handleRazorpayPayment();
    }
  };

  const subtotal = calculateSubtotal();
  const discount = calculateDiscount();
  const shipping = calculateShipping();
  const total = calculateTotal();

  const indianStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  return (
    <>
      <BaseNavbar activeItem="BOOKS" />

      <div className="payment-container">
        {/* Progress Steps */}
        <section className="payment-steps payment-fade-in">
          <div className="step-item" data-active={currentStep >= 1}>
            <div className="step-circle">{currentStep > 1 ? "✓" : "1"}</div>
            <span className="step-label">Billing Details</span>
          </div>
          <div className="step-line" data-active={currentStep >= 2}></div>
          <div className="step-item" data-active={currentStep >= 2}>
            <div className="step-circle">{currentStep > 2 ? "✓" : "2"}</div>
            <span className="step-label">Payment</span>
          </div>
          <div className="step-line" data-active={currentStep >= 3}></div>
          <div className="step-item" data-active={currentStep >= 3}>
            <div className="step-circle">3</div>
            <span className="step-label">Confirmation</span>
          </div>
        </section>

        {loading ? (
          <section className="payment-main-content">
            <div className="payment-form-section">
              <SkeletonLoading
                customStyle={{
                  width: "100%",
                  height: "600px",
                  borderRadius: "20px",
                }}
              />
            </div>
            <div className="payment-summary-section">
              <SkeletonLoading
                customStyle={{
                  width: "100%",
                  height: "400px",
                  borderRadius: "20px",
                }}
              />
            </div>
          </section>
        ) : currentStep === 3 ? (
          // Success Screen
          <section className="payment-success payment-fade-in">
            <div className="success-content">
              <div className="success-icon">✓</div>
              <h2>Payment Successful!</h2>
              <p>Thank you for your purchase. Your order has been confirmed.</p>
              <div className="order-details">
                <p>
                  <strong>Order ID:</strong> #
                  {currentOrder?.order_number ||
                    paymentDetails.razorpay_order_id}
                </p>
                <p>
                  <strong>Payment ID:</strong> #
                  {paymentDetails.razorpay_payment_id}
                </p>
                <p>
                  <strong>Amount Paid:</strong> ₹{total}
                </p>
                <p>
                  <strong>Email:</strong> {billingDetails.email}
                </p>
              </div>
              <p className="success-message">
                A confirmation email has been sent to your registered email
                address. Books have been added to your library!
              </p>
              <div className="success-actions">
                <button
                  className="payment-btn payment-btn-primary"
                  onClick={goToMyBooks}
                >
                  Go to Library
                </button>
                <button
                  className="payment-btn payment-btn-secondary"
                  onClick={() =>
                    navigate(ROUTES.BOOKS_SELLING_HOME_PAGE || "/books")
                  }
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          </section>
        ) : (
          <section className="payment-main-content payment-fade-in">
            {/* Form Section */}
            <div className="payment-form-section">
              {currentStep === 1 && (
                <div className="payment-card">
                  <h2 className="section-title">
                    <span className="title-icon">📋</span>
                    Billing Details
                  </h2>

                  <form
                    className="billing-form"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="first_name">First Name *</label>
                        <input
                          type="text"
                          id="first_name"
                          name="first_name"
                          value={billingDetails.first_name}
                          onChange={handleInputChange}
                          className={formErrors.first_name ? "error" : ""}
                          placeholder="Enter first name"
                        />
                        {formErrors.first_name && (
                          <span className="error-text">
                            {formErrors.first_name}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="last_name">Last Name *</label>
                        <input
                          type="text"
                          id="last_name"
                          name="last_name"
                          value={billingDetails.last_name}
                          onChange={handleInputChange}
                          className={formErrors.last_name ? "error" : ""}
                          placeholder="Enter last name"
                        />
                        {formErrors.last_name && (
                          <span className="error-text">
                            {formErrors.last_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={billingDetails.email}
                          onChange={handleInputChange}
                          className={formErrors.email ? "error" : ""}
                          placeholder="your.email@example.com"
                        />
                        {formErrors.email && (
                          <span className="error-text">{formErrors.email}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={billingDetails.phone}
                          onChange={handleInputChange}
                          className={formErrors.phone ? "error" : ""}
                          placeholder="10-digit mobile number"
                          maxLength="10"
                        />
                        {formErrors.phone && (
                          <span className="error-text">{formErrors.phone}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="address">Street Address *</label>
                      <textarea
                        id="address"
                        name="address"
                        value={billingDetails.address}
                        onChange={handleInputChange}
                        className={formErrors.address ? "error" : ""}
                        placeholder="House no., Building name, Street"
                        rows="3"
                      />
                      {formErrors.address && (
                        <span className="error-text">{formErrors.address}</span>
                      )}
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="city">City *</label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={billingDetails.city}
                          onChange={handleInputChange}
                          className={formErrors.city ? "error" : ""}
                          placeholder="Enter city"
                        />
                        {formErrors.city && (
                          <span className="error-text">{formErrors.city}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="state">State *</label>
                        <select
                          id="state"
                          name="state"
                          value={billingDetails.state}
                          onChange={handleInputChange}
                          className={formErrors.state ? "error" : ""}
                        >
                          <option value="">Select State</option>
                          {indianStates.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        {formErrors.state && (
                          <span className="error-text">{formErrors.state}</span>
                        )}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="pincode">Pincode *</label>
                        <input
                          type="text"
                          id="pincode"
                          name="pincode"
                          value={billingDetails.pincode}
                          onChange={handleInputChange}
                          className={formErrors.pincode ? "error" : ""}
                          placeholder="6-digit pincode"
                          maxLength="6"
                        />
                        {formErrors.pincode && (
                          <span className="error-text">
                            {formErrors.pincode}
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="country">Country</label>
                        <input
                          type="text"
                          id="country"
                          name="country"
                          value={billingDetails.country}
                          disabled
                          className="disabled-input"
                        />
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={agreeTerms}
                          onChange={(e) => {
                            setAgreeTerms(e.target.checked);
                            if (formErrors.terms) {
                              setFormErrors((prev) => ({ ...prev, terms: "" }));
                            }
                          }}
                        />
                        <span>
                          I agree to the{" "}
                          <a href="/terms" target="_blank">
                            Terms & Conditions
                          </a>{" "}
                          and{" "}
                          <a href="/privacy" target="_blank">
                            Privacy Policy
                          </a>{" "}
                          *
                        </span>
                      </label>
                      {formErrors.terms && (
                        <span className="error-text">{formErrors.terms}</span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="payment-btn payment-btn-primary btn-full"
                      onClick={handleContinueToPayment}
                    >
                      Continue to Payment →
                    </button>
                  </form>
                </div>
              )}

              {currentStep === 2 && (
                <div className="payment-card">
                  <h2 className="section-title">
                    <span className="title-icon">💳</span>
                    Payment Method
                  </h2>

                  {/* Show retry option if payment failed */}
                  {orderStatus === "failed" && canRetry && (
                    <div className="payment-error-banner">
                      <div className="error-content">
                        <span className="error-icon">⚠️</span>
                        <div>
                          <h4>Payment Failed</h4>
                          <p>
                            {paymentError?.description ||
                              "Your previous payment attempt failed."}
                          </p>
                        </div>
                      </div>
                      <button
                        className="retry-btn"
                        onClick={handleRetryPayment}
                        disabled={processingPayment}
                      >
                        {processingPayment ? "Retrying..." : "Retry Payment"}
                      </button>
                    </div>
                  )}

                  <div className="payment-methods">
                    <div
                      className={`payment-method-card ${
                        paymentMethod === "razorpay" ? "active" : ""
                      }`}
                      onClick={() => setPaymentMethod("razorpay")}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="razorpay"
                        checked={paymentMethod === "razorpay"}
                        onChange={() => setPaymentMethod("razorpay")}
                      />
                      <div className="method-content">
                        <div className="method-header">
                          <img
                            src="https://via.placeholder.com/60x20/0C2451/ffffff?text=Razorpay"
                            alt="Razorpay"
                          />
                          <span className="recommended-badge">Recommended</span>
                        </div>
                        <p className="method-description">
                          Pay securely using Credit/Debit Card, UPI, Net
                          Banking, or Wallet
                        </p>
                        <div className="payment-icons">
                          <span>💳</span>
                          <span>📱</span>
                          <span>🏦</span>
                          <span>👛</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="billing-address-summary">
                    <h3>Delivery Address</h3>
                    <div className="address-box">
                      <p>
                        <strong>
                          {billingDetails.first_name} {billingDetails.last_name}
                        </strong>
                      </p>
                      <p>{billingDetails.address}</p>
                      <p>
                        {billingDetails.city}, {billingDetails.state} -{" "}
                        {billingDetails.pincode}
                      </p>
                      <p>📞 {billingDetails.phone}</p>
                      <p>✉️ {billingDetails.email}</p>
                    </div>
                    <button
                      className="edit-address-btn"
                      onClick={() => setCurrentStep(1)}
                    >
                      ✏️ Edit Address
                    </button>
                  </div>
                  <button
                    type="button"
                    className="payment-btn payment-btn-checkout btn-full"
                    onClick={handlePlaceOrder}
                    disabled={processingPayment}
                  >
                    {processingPayment ? (
                      <>
                        <span className="spinner"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        {paymentMethod === "razorpay"
                          ? "Pay Now"
                          : "Place Order"}{" "}
                        - ₹{total.toFixed(2)}
                      </>
                    )}
                  </button>

                  <div className="secure-payment-notice">
                    <span>🔒</span>
                    <p>Your payment information is secure and encrypted</p>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="payment-summary-section">
              <div className="payment-summary-card">
                <h2 className="summary-title">Order Summary</h2>

                {/* Order Status Indicator */}
                {orderStatus && (
                  <div className={`order-status-badge status-${orderStatus}`}>
                    <span className="status-dot"></span>
                    <span className="status-text">
                      {orderStatus === "pending" && "Order Created"}
                      {orderStatus === "processing" && "Processing Payment"}
                      {orderStatus === "paid" && "Payment Successful"}
                      {orderStatus === "failed" && "Payment Failed"}
                    </span>
                  </div>
                )}

                <div className="order-items">
                  {cartItems?.map((item) => (
                    <div key={item.id} className="summary-item">
                      <img src={item.cover_image_url} alt={item.title} />
                      <div className="item-details">
                        <h4>{item.title}</h4>
                        <p>Qty: 1</p>
                      </div>
                      <div className="item-price">
                        ₹{parseFloat(item.price).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="summary-breakdown">
                  <div className="summary-row">
                    <span>Subtotal ({cartItems?.length} items)</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>

                  {discount > 0 && (
                    <div className="summary-row discount-row">
                      <span>Discount</span>
                      <span className="discount-value">
                        −₹{discount.toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className="summary-row">
                    <span>Shipping</span>
                    <span className={shipping === 0 ? "free-shipping" : ""}>
                      {shipping === 0 ? "FREE" : `₹${shipping.toFixed(2)}`}
                    </span>
                  </div>

                  <div className="summary-divider"></div>

                  <div className="summary-row summary-total">
                    <span>Total Amount</span>
                    <span className="total-amount">₹{total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="trust-badges">
                  <div className="trust-badge">
                    <span className="badge-icon">✓</span>
                    <span>Secure Payment</span>
                  </div>
                  <div className="trust-badge">
                    <span className="badge-icon">✓</span>
                    <span>Instant Access</span>
                  </div>
                  <div className="trust-badge">
                    <span className="badge-icon">✓</span>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Notification */}
        {notification.visible && (
          <div className={`payment-notification ${notification.type}`}>
            {notification.message}
          </div>
        )}
      </div>

      <Footer />
    </>
  );
};

export default BookPaymentPage;
